import {useState} from 'react';

import i18n from '@dms/i18n';

import {parseDate, useInterval} from 'shared/web';

const SECOND_MS = 1000;
const HOUR_MS = 60 * 60 * SECOND_MS;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_DAY = 86400;

/** Auctions ending within this threshold are considered "ending soon". */
const ENDING_SOON_THRESHOLD_MS = HOUR_MS;

function computeSegments(diffMs: number) {
  const totalSeconds = Math.floor(diffMs / SECOND_MS);
  return {
    days: Math.floor(totalSeconds / SECONDS_IN_DAY),
    hours: Math.floor((totalSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR),
    minutes: Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE),
    seconds: totalSeconds % SECONDS_IN_MINUTE,
  };
}

function formatCountdown(segments: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): string {
  if (segments.days > 0) {
    return `${segments.days}d ${segments.hours}h`;
  }
  if (segments.hours > 0) {
    return `${segments.hours}h ${segments.minutes}m`;
  }
  if (segments.minutes > 0) {
    return `${segments.minutes}m ${segments.seconds}s`;
  }
  return `${segments.seconds}s`;
}

export interface AuctionCountdownState {
  label: string;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True when the auction end time has passed. */
  isEnded: boolean;
  /** True when the auction ends in less than 1 hour (but has not ended yet). */
  isEndingSoon: boolean;
}

function createEndedState(): AuctionCountdownState {
  return {
    label: i18n.t('general.labels.closed'),
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isEnded: true,
    isEndingSoon: false,
  };
}

function computeState(endTime: string): AuctionCountdownState {
  const parsed = parseDate(endTime);
  if (parsed === null || isNaN(parsed.getTime())) {
    return createEndedState();
  }
  const diff = parsed.getTime() - Date.now();
  if (diff <= 0) {
    return createEndedState();
  }
  const segments = computeSegments(diff);
  return {
    label: formatCountdown(segments),
    ...segments,
    isEnded: false,
    isEndingSoon: diff < ENDING_SOON_THRESHOLD_MS,
  };
}

/**
 * Returns a live countdown state for the given ISO 8601 end time.
 * Updates every second while the auction is still live.
 *
 * - `isEnded`: true once the end time has passed.
 * - `isEndingSoon`: true when less than 1 hour remains.
 * - `label`: human-readable countdown string (e.g. "2h 5m", "45m 30s", "30s").
 */
export function useAuctionCountdown(endTime: string): AuctionCountdownState {
  const [prevEndTime, setPrevEndTime] = useState(endTime);
  const [state, setState] = useState<AuctionCountdownState>(() => computeState(endTime));

  if (endTime !== prevEndTime) {
    setPrevEndTime(endTime);
    setState(computeState(endTime));
  }

  useInterval(
    () => {
      setState(computeState(endTime));
    },
    state.isEnded ? null : SECOND_MS
  );

  return state;
}
